import { useCallback, useEffect, useState } from 'react';
import axios from '../config/axios';
import { getApiUrl } from '../config/environment';

export type AsaasBillingType = 'UNDEFINED' | 'PIX' | 'BOLETO' | 'CREDIT_CARD';
export type AsaasChargeStatus = 'pending' | 'overdue' | 'paid' | 'cancelled' | 'refunded' | 'chargeback';

export const ASAAS_BILLING_TYPE_LABELS: Record<AsaasBillingType, string> = {
  UNDEFINED: 'Link (paciente escolhe PIX, boleto ou cartão)',
  PIX: 'PIX',
  BOLETO: 'Boleto',
  CREDIT_CARD: 'Cartão de crédito',
};

export const ASAAS_CHARGE_STATUS_META: Record<AsaasChargeStatus, { label: string; color: 'default' | 'warning' | 'success' | 'error' | 'info' }> = {
  pending: { label: 'Aguardando pagamento', color: 'warning' },
  overdue: { label: 'Vencida', color: 'error' },
  paid: { label: 'Pago', color: 'success' },
  cancelled: { label: 'Cancelada', color: 'default' },
  refunded: { label: 'Estornada', color: 'info' },
  chargeback: { label: 'Contestada', color: 'error' },
};

export interface AsaasAccount {
  id: string;
  unit_id: string | null;
  scope: 'unit' | 'all';
  environment: 'production' | 'sandbox';
  api_key_last4: string | null;
  account_name: string | null;
  account_document: string | null;
  account_email: string | null;
  has_pix_key: boolean | null;
  webhook_url: string | null;
  webhook_status: string | null;
  bank_account_id: string | null;
  allowed_billing_types: AsaasBillingType[];
  default_billing_type: AsaasBillingType;
  due_days: number;
  fine_percent: number;
  interest_percent: number;
  asaas_notifications: boolean;
  message_template: string;
  send_paid_confirmation: boolean;
  asaas_split_repasse_enabled: boolean;
  enabled: boolean;
  last_error: string | null;
  last_validated_at: string | null;
  updated_at: string;
}

export interface AsaasStatus {
  installed: boolean;
  connected: boolean;
  account: AsaasAccount | null;
  inherited: boolean;
  webhook_base_url: string | null;
  billing_types: AsaasBillingType[];
}

export interface ProfessionalAsaasStatus {
  clinic_asaas_enabled: boolean;
  connected: boolean;
  wallet_id: string | null;
  environment: 'production' | 'sandbox' | null;
  account_name: string | null;
  api_key_last4: string | null;
  linked_at: string | null;
  link_error: string | null;
}

export interface AsaasRepasseSplitSnapshot {
  enabled: boolean;
  fingerprint: string;
  professional_id: string | null;
  professional_name: string | null;
  wallet_id: string | null;
  act_type: string | null;
  calc_type: 'percentage' | 'fixed' | null;
  percentage: number | null;
  fixed_value: number | null;
  gross_base: number;
  net_base: number;
  expected_commission: number;
  rule_id: string | null;
  skipped_reason: string | null;
}

export interface BudgetRepasseContext {
  budget_id: string;
  budget_number: string | null;
  needs_execution_ack: boolean;
  pending_items: Array<{
    id: string;
    procedure_name: string | null;
    status: string;
    has_execution: boolean;
  }>;
}

export interface AsaasKlinviaPlatformSplitSnapshot {
  enabled: boolean;
  fingerprint: string;
  wallet_id: string | null;
  percent: number | null;
  skipped_reason: string | null;
}

export interface AsaasRepassePreview {
  split_enabled: boolean;
  open_balance: number;
  split: unknown[] | null;
  snapshot: AsaasRepasseSplitSnapshot;
  platform_split?: AsaasKlinviaPlatformSplitSnapshot | null;
  budget_repasse?: BudgetRepasseContext | null;
}

export interface AsaasCharge {
  id: string;
  transaction_id: string | null;
  patient_id: string | null;
  unit_id: string | null;
  asaas_payment_id: string;
  billing_type: AsaasBillingType;
  billing_type_label: string;
  value: number;
  value_cents: number;
  net_value: number | null;
  due_date: string;
  description: string | null;
  status: AsaasChargeStatus;
  asaas_status: string | null;
  invoice_url: string | null;
  bank_slip_url: string | null;
  pix_payload: string | null;
  pix_qr_image_url: string | null;
  pix_qr_base64?: string | null;
  pix_expiration: string | null;
  paid_at: string | null;
  paid_billing_type: string | null;
  settled: boolean;
  settlement_error: string | null;
  sent_whatsapp_at: string | null;
  sent_whatsapp_provider: string | null;
  sent_whatsapp_phone: string | null;
  sent_whatsapp_error: string | null;
  created_by_name: string | null;
  created_at: string;
  updated_at: string;
}

export interface AsaasWhatsAppResult {
  sent: boolean;
  provider?: string;
  via_template?: boolean;
  conversation_id?: string | null;
  phone?: string;
  error?: string;
  code?: string | null;
}

export interface AsaasChargeResult {
  charge: AsaasCharge;
  reused: boolean;
  whatsapp: AsaasWhatsAppResult | null;
}

/** Mensagem e código de erro vindos da API (`{ error, code, details }`). */
export function asaasApiError(err: any): { message: string; code: string | null; details: any } {
  const data = err?.response?.data || {};
  return {
    message: data.error || data.message || err?.message || 'Erro na integração Asaas.',
    code: data.code || null,
    details: data.details || null,
  };
}

const statusCache = new Map<string, Promise<AsaasStatus>>();

function unitKey(): string {
  try {
    const raw = localStorage.getItem('auth_user');
    return raw ? String(JSON.parse(raw)?.current_unit?.id || 'default') : 'default';
  } catch {
    return 'default';
  }
}

export function invalidateAsaasStatusCache(): void {
  statusCache.clear();
  window.dispatchEvent(new CustomEvent('klinvia:asaas-status-changed'));
}

export const asaasService = {
  async getStatus(force = false): Promise<AsaasStatus> {
    const key = unitKey();
    if (force) statusCache.delete(key);
    let pending = statusCache.get(key);
    if (!pending) {
      pending = axios.get(getApiUrl('/clinic/asaas/account')).then((r) => r.data.data as AsaasStatus);
      pending.catch(() => statusCache.delete(key));
      statusCache.set(key, pending);
    }
    return pending;
  },

  async saveAccount(body: Record<string, unknown>) {
    const r = await axios.put(getApiUrl('/clinic/asaas/account'), body);
    invalidateAsaasStatusCache();
    return r.data.data as { account: AsaasAccount; warnings: string[] };
  },

  async testAccount() {
    const r = await axios.post(getApiUrl('/clinic/asaas/account/test'));
    invalidateAsaasStatusCache();
    return r.data.data as { account: AsaasAccount; warnings: string[] };
  },

  async disconnect(scope: 'unit' | 'all') {
    const r = await axios.delete(getApiUrl('/clinic/asaas/account'), { params: { scope } });
    invalidateAsaasStatusCache();
    return r.data.data as { disconnected: boolean };
  },

  async getProfessionalAsaas(professionalId: string) {
    const r = await axios.get(getApiUrl(`/clinic/professionals/${professionalId}/asaas`));
    return r.data.data as ProfessionalAsaasStatus;
  },

  async connectProfessionalAsaas(professionalId: string, apiKey: string) {
    const r = await axios.post(getApiUrl(`/clinic/professionals/${professionalId}/asaas`), { api_key: apiKey });
    return r.data.data as ProfessionalAsaasStatus;
  },

  async disconnectProfessionalAsaas(professionalId: string) {
    const r = await axios.delete(getApiUrl(`/clinic/professionals/${professionalId}/asaas`));
    return r.data.data as ProfessionalAsaasStatus;
  },

  async listCharges(params: { patient_id?: string; transaction_ids?: string[]; status?: string; limit?: number }) {
    const r = await axios.get(getApiUrl('/clinic/asaas/charges'), {
      params: {
        patient_id: params.patient_id,
        transaction_ids: params.transaction_ids?.length ? params.transaction_ids.join(',') : undefined,
        status: params.status,
        limit: params.limit,
      },
    });
    return (r.data.data || []) as AsaasCharge[];
  },

  async getCharge(id: string, refresh = false) {
    const r = await axios.get(getApiUrl(`/clinic/asaas/charges/${id}`), { params: refresh ? { refresh: 1 } : undefined });
    return r.data.data as AsaasCharge;
  },

  async previewRepasseSplit(
    transactionId: string,
    repasseProfessionalId?: string | null,
    opts?: { assumeBudgetExecuted?: boolean },
  ) {
    const r = await axios.get(getApiUrl('/clinic/asaas/charges/repasse-preview'), {
      params: {
        transaction_id: transactionId,
        repasse_professional_id: repasseProfessionalId || undefined,
        assume_budget_executed: opts?.assumeBudgetExecuted ? '1' : undefined,
      },
    });
    return r.data.data as AsaasRepassePreview;
  },

  async createCharge(body: {
    transaction_id: string;
    billing_type?: AsaasBillingType;
    due_date?: string;
    description?: string;
    send_whatsapp?: boolean;
    channel_id?: string;
    phone?: string;
    patient_cpf?: string;
    idempotency_key?: string;
    repasse_professional_id?: string;
    confirm_mark_budget_executed?: boolean;
  }) {
    const r = await axios.post(getApiUrl('/clinic/asaas/charges'), body);
    return r.data.data as AsaasChargeResult;
  },

  async createStandaloneCharge(body: {
    patient_id: string;
    amount: number;
    description: string;
    category?: string;
    billing_type?: AsaasBillingType;
    due_date?: string;
    send_whatsapp?: boolean;
    channel_id?: string;
    phone?: string;
    patient_cpf?: string;
    idempotency_key?: string;
    repasse_professional_id?: string;
  }) {
    const r = await axios.post(getApiUrl('/clinic/asaas/charges/standalone'), body);
    return r.data.data as AsaasChargeResult;
  },

  async sendWhatsApp(id: string, body: { channel_id?: string; phone?: string }) {
    const r = await axios.post(getApiUrl(`/clinic/asaas/charges/${id}/send-whatsapp`), body);
    return r.data.data as AsaasWhatsAppResult;
  },

  async cancelCharge(id: string, reason?: string) {
    const r = await axios.post(getApiUrl(`/clinic/asaas/charges/${id}/cancel`), { reason });
    return r.data.data as AsaasCharge;
  },
};

/** Conta Asaas da unidade ativa (cache compartilhado entre as telas). */
export function useAsaasStatus(enabled = true) {
  const [status, setStatus] = useState<AsaasStatus | null>(null);
  const [loading, setLoading] = useState(enabled);

  const load = useCallback(
    (force = false) => {
      if (!enabled) return;
      setLoading(true);
      asaasService
        .getStatus(force)
        .then(setStatus)
        .catch(() => setStatus(null))
        .finally(() => setLoading(false));
    },
    [enabled],
  );

  useEffect(() => {
    load();
    const onChange = () => load();
    window.addEventListener('klinvia:asaas-status-changed', onChange);
    window.addEventListener('unit:changed', onChange);
    return () => {
      window.removeEventListener('klinvia:asaas-status-changed', onChange);
      window.removeEventListener('unit:changed', onChange);
    };
  }, [load]);

  return { status, connected: !!status?.connected, loading, reload: () => load(true) };
}

export function formatBRL(value: number): string {
  return (Number(value) || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export function formatDateBR(iso: string | null | undefined): string {
  const s = String(iso || '').slice(0, 10);
  const [y, m, d] = s.split('-');
  return y && m && d ? `${d}/${m}/${y}` : s;
}

/** Saldo em aberto de uma conta a receber, igual ao cálculo do backend. */
export function receivableOpenBalance(row: Record<string, any>): number {
  const amount = Number(row?.amount) || 0;
  const discount = Number(row?.discount) || 0;
  const interest = Number(row?.interest) || 0;
  const penalty = Number(row?.penalty) || 0;
  const paid = Number(row?.amount_paid ?? row?.paid_amount) || 0;
  return Math.round((amount - discount + interest + penalty - paid) * 100) / 100;
}
